import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Minus,
  Clock,
  Repeat,
  FileAudio,
  Sparkles,
  Star,
  Zap,
  ArrowUpCircle,
  ArrowDownCircle,
  XCircle,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, cn } from '../components/ui';
import RedemptionTerms from '../components/RedemptionTerms';
import { linkButtonClass } from '../utils/linkButtonClass';

/**
 * 料金プラン詳細ページ（/pricing）。
 *
 * トップページの料金セクションが「選ぶ前の一覧」なのに対し、本ページは
 * 「決める前に読む」詳細版。プランごとの上限・機能差・変更/解約の扱いまでを扱う。
 *
 * 数値はバックエンド設定（backend/app/core/config.py の *_PLAN_SESSIONS /
 * *_PLAN_HOURS / *_PLAN_PRICE / ONESHOT_PRICE_PER_HOUR）と、機能ゲート
 * （diarization=standard,unlimited / manual_edit=unlimited /
 * audio_retention=unlimited）に対応する。「混雑時の処理優先」は
 * backend/app/services/queue_priority.py の PRIORITY_PLANS（unlimited のみ優先）に対応する。
 * その発現条件（比較表の note に書いた「順番待ちが2件以上」）は
 * backend/app/tasks/celery_app.py の worker_prefetch_multiplier=1 と task_acks_late 未設定
 * （＝ワーカーが常に実行中1件＋予約1件を保持する）にも依存する。
 * 片方だけ変えると表示が実挙動とずれるため、
 * 設定を変更したときは本ページの表記も併せて更新すること。
 */

interface Plan {
  key: string;
  name: string;
  price: string;
  priceNote: string;
  summary: string;
  /** アイコン背景色（トップページの料金セクションと同じ配色で揃える） */
  color: string;
  icon: JSX.Element;
  points: string[];
  popular?: boolean;
}

const plans: Plan[] = [
  {
    key: 'free',
    name: '無料プラン',
    price: '¥0',
    priceNote: 'ずっと無料',
    summary: 'まずは書き起こしの精度を試したい方に',
    color: '#3b82f6',
    icon: <FileAudio className="w-7 h-7 text-white" />,
    points: [
      '月3回まで書き起こし',
      '月の合計15分まで',
      'タイムスタンプ付きテキスト',
      'TXT / JSON / HTML でダウンロード',
    ],
  },
  {
    key: 'lite',
    name: 'ライトプラン',
    price: '¥500',
    priceNote: '月額（税込）',
    summary: '月に1〜3回、セッションを記録する方に',
    color: '#059669',
    icon: <Sparkles className="w-7 h-7 text-white" />,
    points: [
      '月3回まで書き起こし',
      '月の合計9時間まで',
      '1回の長さに制限なし（ファイルは最大2GB）',
      'タイムスタンプ付きテキスト',
    ],
  },
  {
    key: 'standard',
    name: 'スタンダードプラン',
    price: '¥1,000',
    priceNote: '月額（税込）',
    summary: '複数人のセッションを、話者ごとに残したい方に',
    color: '#ef6c43',
    icon: <Star className="w-7 h-7 text-white" />,
    points: [
      '月6回まで書き起こし',
      '月の合計18時間まで',
      '話者分離（誰の発言かを自動で分ける）',
      '話者名の編集（「話者1」→ キャラクター名）',
    ],
    popular: true,
  },
  {
    key: 'unlimited',
    name: '無制限プラン',
    price: '¥4,000',
    priceNote: '月額（税込）',
    summary: '毎週卓を囲む方、記録をしっかり仕上げたい方に',
    color: '#8b5cf6',
    icon: <Zap className="w-7 h-7 text-white" />,
    points: [
      '回数・時間ともに無制限',
      'テキスト再生（本文から音声を聞き直す）',
      'テキスト編集（誤変換をその場で直す）',
      '結果の保持期間が7日間に延長（他プランは8時間）',
      '混雑時の処理優先（実行中の処理に割り込むものではありません）',
    ],
  },
];

/** 比較表のセル値。true=対応 / false=非対応 / 文字列=条件つき */
type Cell = boolean | string;

interface CompareRow {
  label: string;
  note?: string;
  values: [Cell, Cell, Cell, Cell]; // free, lite, standard, unlimited
}

const compareRows: CompareRow[] = [
  {
    label: '月額料金',
    values: ['¥0', '¥500', '¥1,000', '¥4,000'],
  },
  {
    label: '書き起こし回数',
    note: '毎月リセット（繰り越しなし）',
    values: ['月3回', '月3回', '月6回', '無制限'],
  },
  {
    label: '月の合計時間',
    note: '毎月リセット（繰り越しなし）',
    values: ['15分', '9時間', '18時間', '無制限'],
  },
  {
    label: '1回あたりの長さ',
    note: '長さの上限はなく、実質の上限はファイルサイズ',
    values: ['最大2GB', '最大2GB', '最大2GB', '最大2GB'],
  },
  {
    label: 'タイムスタンプ付きテキスト',
    values: [true, true, true, true],
  },
  {
    label: 'TXT / JSON / HTML 出力',
    values: [true, true, true, true],
  },
  {
    label: '話者分離',
    note: '誰が話したかを自動で分ける',
    values: [false, false, true, true],
  },
  {
    label: '話者名の編集',
    note: '「話者1」をキャラクター名に付け替える',
    values: [false, false, true, true],
  },
  {
    label: 'テキスト編集',
    note: '本文の誤変換や話者の付け替えを手で直す',
    values: [false, false, false, true],
  },
  {
    label: 'テキスト再生',
    note: '本文をクリックしてその場面の音声を聞き直す',
    values: [false, false, false, true],
  },
  {
    label: '書き起こし結果の保持',
    values: ['8時間', '8時間', '8時間', '7日間'],
  },
  {
    label: '音声の保持',
    note: '聞き直し用。期限を過ぎると結果とともに自動削除',
    values: ['処理後に削除', '処理後に削除', '処理後に削除', '7日間'],
  },
  {
    label: '混雑時の処理優先',
    note: '順番待ちが2件以上たまっているときに、通常プランより先に処理されます。実行中の処理に割り込むものではありません（次に取りかかる分にも割り込みません）',
    values: [false, false, false, true],
  },
];

const planColumns = ['無料', 'ライト', 'スタンダード', '無制限'];

function CompareCell({ value }: { value: Cell }) {
  if (value === true) {
    return (
      <>
        <Check className="w-5 h-5 text-emerald-500 inline-block" aria-hidden="true" />
        <span className="sr-only">対応</span>
      </>
    );
  }
  if (value === false) {
    return (
      <>
        <Minus className="w-5 h-5 text-text-sub/40 inline-block" aria-hidden="true" />
        <span className="sr-only">非対応</span>
      </>
    );
  }
  return <span className="text-sm text-text">{value}</span>;
}

export default function PricingPage() {
  return (
    <Layout showPromo>
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-hover transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            トップに戻る
          </Link>
        </div>
      </header>

      <main className="w-full max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-text-heading mb-3">料金プラン</h1>
          <p className="text-text-sub leading-relaxed">
            セッションの頻度に合わせて選べます。いつでも変更・解約できます。
            <span className="block">日本語の音声のみ対応しています（Japanese audio only）</span>
          </p>
        </div>

        {/* プランカード */}
        <section aria-labelledby="plans-heading" className="mb-16">
          <h2 id="plans-heading" className="sr-only">
            プラン一覧
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.key}
                className={cn(
                  'relative flex flex-col',
                  plan.popular && 'border-2 border-primary shadow-xl'
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-6 bg-primary text-white px-4 py-1.5 rounded-3xl text-xs font-semibold flex items-center gap-1.5 shadow-lg">
                    <Star className="w-3.5 h-3.5" aria-hidden="true" />
                    <span>人気No.1</span>
                  </div>
                )}
                <div className="flex items-center gap-4 mb-4 mt-1">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text-heading">{plan.name}</h3>
                    <p className="text-sm text-text-sub">{plan.summary}</p>
                  </div>
                </div>
                <div className="mb-5">
                  <span className="text-3xl font-extrabold text-text-heading">{plan.price}</span>
                  <span className="ml-2 text-sm text-text-sub">{plan.priceNote}</span>
                </div>
                <ul className="space-y-2.5">
                  {plan.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-text-sub">
                      <Check
                        className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>

        {/* 比較表 */}
        <section aria-labelledby="compare-heading" className="mb-16">
          <h2 id="compare-heading" className="text-2xl font-bold text-text-heading mb-2">
            プラン比較
          </h2>
          <p className="text-sm text-text-sub mb-5">
            回数・時間は毎月リセットされ、残りが翌月や変更後のプランへ繰り越されることはありません。
          </p>
          {/* 幅の狭い端末では表だけを横スクロールさせる（ページ全体は横に伸ばさない） */}
          <div className="overflow-x-auto rounded-panel shadow-card bg-card">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <caption className="sr-only">プラン別の上限と対応機能の比較表</caption>
              <thead>
                <tr className="border-b border-text-sub/15">
                  <th scope="col" className="px-5 py-4 text-sm font-bold text-text-heading">
                    項目
                  </th>
                  {planColumns.map((name) => (
                    <th
                      key={name}
                      scope="col"
                      className="px-4 py-4 text-center text-sm font-bold text-text-heading whitespace-nowrap"
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row) => (
                  <tr key={row.label} className="border-b border-text-sub/10 last:border-b-0">
                    <th scope="row" className="px-5 py-4 align-top font-medium">
                      <span className="block text-sm text-text-heading">{row.label}</span>
                      {row.note && (
                        <span className="block text-xs text-text-sub mt-0.5 font-normal">
                          {row.note}
                        </span>
                      )}
                    </th>
                    {row.values.map((value, i) => (
                      <td key={planColumns[i]} className="px-4 py-4 text-center align-middle">
                        <CompareCell value={value} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-text-sub mt-4">
            話者分離・テキスト再生・テキスト編集が実際にどう動くかは、
            <Link
              to="/guide"
              className="mx-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
            >
              機能ガイド
            </Link>
            で詳しく紹介しています。
          </p>
        </section>

        {/* ワンショット課金 */}
        <section aria-labelledby="oneshot-heading" className="mb-16">
          <h2 id="oneshot-heading" className="text-2xl font-bold text-text-heading mb-5">
            必要なときだけ：時間・回数の追加購入
          </h2>
          <Card className="flex flex-col md:flex-row md:items-center gap-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#ec4899' }}
            >
              <Clock className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-extrabold text-primary">¥120</span>
                <span className="text-sm text-text-sub">／1時間（税込）</span>
              </div>
              <p className="text-sm text-text-sub leading-relaxed">
                月額プランを契約せず、必要な時間だけ購入して使えます。月額プランとの併用も可能で（無制限プランを除く）、
                購入した時間は月間枠とは<span className="font-bold text-text-heading">別枠</span>
                として扱われ、使い切るまで保持されます（更新日をまたいでも失われません）。
              </p>
            </div>
          </Card>
          {/* 回数の購入はライト・スタンダードのみ（billing.create_oneshot でフリー・無制限は 403） */}
          <Card className="flex flex-col md:flex-row md:items-center gap-6 mt-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#ec4899' }}
            >
              <Repeat className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-extrabold text-primary">¥100</span>
                <span className="text-sm text-text-sub">／1回（税込）</span>
              </div>
              <p className="text-sm text-text-sub leading-relaxed">
                ライト・スタンダードプランをご契約中の方向けです。月の書き起こし回数を使い切ったあとも、時間が残っていれば回数だけを追加して書き起こせます。購入した回数は
                <span className="font-bold text-text-heading">無期限</span>
                で、月の回数を使い切ったあとに1回ずつ使われます（更新日をまたいでも失われません）。
              </p>
            </div>
          </Card>
        </section>

        {/* プラン変更・解約 */}
        <section aria-labelledby="change-heading" className="mb-16">
          <h2 id="change-heading" className="text-2xl font-bold text-text-heading mb-5">
            プラン変更と解約
          </h2>
          <div className="space-y-4">
            {[
              {
                icon: <ArrowUpCircle className="w-6 h-6 text-white" aria-hidden="true" />,
                title: 'アップグレード（上位プランへ）',
                body: 'その場ですぐ適用され、利用枠の上限が新しいプランに引き上がります。残り期間の差額が日割りで請求されます。その月に使った時間はそのまま引き継がれます（上限だけが上がり、使用済みの時間はリセットされません）。',
              },
              {
                icon: <ArrowDownCircle className="w-6 h-6 text-white" aria-hidden="true" />,
                title: 'ダウングレード（下位プランへ）',
                body: '次回更新日から適用されます。それまでは現在のプランのままご利用いただけるので、支払い済みの期間が無駄になることはありません。',
              },
              {
                icon: <XCircle className="w-6 h-6 text-white" aria-hidden="true" />,
                title: '解約',
                body: 'いつでも解約できます。解約後も契約期間の終了日まではそのままご利用いただけます。期間終了後は無料プランに戻ります。',
              },
            ].map((item) => (
              <Card key={item.title} className="flex gap-5">
                <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-base font-bold text-text-heading mb-1.5">{item.title}</h3>
                  <p className="text-sm text-text-sub leading-relaxed">{item.body}</p>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-sm text-text-sub mt-4">
            支払いは Stripe を通じて処理され、クレジットカード情報が otomochi
            のサーバーに保存されることはありません。
          </p>
        </section>

        {/* 特典コードのご利用条件（Unit 003）
            ログイン不要な場所への掲示。/profile はログイン必須ルートのため、譲渡された未登録者が
            引き換え前に条件を読めない。資金決済法の整理（無償・転売禁止）を支えるにはここが必要。
            折りたたまない（掲示が section の主目的で、閉じた既定だと掲示としての実効性が下がる）。 */}
        <section aria-labelledby="redeem-terms-heading" className="mb-16">
          <h2 id="redeem-terms-heading" className="text-2xl font-bold text-text-heading mb-5">
            特典コードのご利用条件
          </h2>
          <Card>
            <RedemptionTerms />
          </Card>
        </section>

        {/* CTA */}
        <Card className="text-center py-12 px-6">
          <h2 className="text-2xl font-bold text-text-heading mb-3">まずは無料で試せます</h2>
          <p className="text-text-sub mb-8 leading-relaxed">
            無料プランなら、月3回・合計15分まで書き起こしをお試しいただけます。
            <br className="hidden sm:inline" />
            精度を確かめてから、必要に応じてプランを選んでください。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/login" className={linkButtonClass('primary')}>
              <span>無料で始める</span>
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </Link>
            <Link to="/guide" className={linkButtonClass('secondary')}>
              機能ガイドを見る
            </Link>
          </div>
          <p className="text-sm text-text-sub mt-8">
            料金についての疑問は
            <Link
              to="/faq"
              className="mx-1 text-accent hover:text-accent-hover font-medium underline underline-offset-2"
            >
              よくある質問
            </Link>
            もご覧ください。
          </p>
        </Card>
      </main>
    </Layout>
  );
}
